import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getErrorMessage } from 'src/Utils/messageUtils';

interface Company {
  Company_Id: number;
  Is_Enable: boolean;
  [key: string]: any; // Extendable for future fields
}

interface SnackbarState {
  show: boolean;
  message: string;
  type: 'Success' | 'Error' | 'Info';
}

interface CompanyListState {
  companyData: Company[];
  loading: boolean;
  updateLoading: boolean;
  updatingCompanyId: number | null;
  error: string | null;
  snackbar: SnackbarState;
}

const initialState: CompanyListState = {
  companyData: [],
  loading: false,
  updateLoading: false,
  updatingCompanyId: null,
  error: null,
  snackbar: { show: false, message: '', type: 'Info' },
};

const companyListSlice = createSlice({
  name: 'companyList',
  initialState,
  reducers: {
    fetchCompanyListRequest: (
      state,
      action: PayloadAction<{
        search: string;
        page: number;
        perPage: number;
        sortBy?: string;
        sortOrder?: 'ascend' | 'descend';
      }>
    ) => {
      state.loading = true;
      state.error = null;
    },
    fetchCompanyListSuccess: (state, action: PayloadAction<{ data: Company[] }>) => {
      state.loading = false;
      state.companyData = action.payload;
    },
    fetchCompanyListFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.error = action.payload;
    },

    updateCompanyStatusRequest: (
      state,
      action: PayloadAction<{
        companyId: number;
        isEnabled: boolean;
        search: string;
        page: number;
        perPage: number;
        sortBy?: string;
        sortOrder?: string;
      }>
    ) => {
      state.updateLoading = true;
      state.updatingCompanyId = action.payload.companyId;
      state.error = null;
    },
    updateCompanyStatusSuccess: (state, action: PayloadAction<{ companyId: number; isEnabled: boolean; message: string }>) => {
      state.updateLoading = false;
      state.updatingCompanyId = null;
      const { companyId, isEnabled, message } = action.payload;

      if (state.companyData?.data) {
        state.companyData.data = state.companyData.data.map((company: Company) =>
          company.Company_Id === companyId ? { ...company, Is_Enable: isEnabled } : company
        );
      }

      state.snackbar = {
        show: true,
        message: getErrorMessage(message).message,
        type: getErrorMessage(message).type,
      };
    },
    updateCompanyStatusFailure: (state, action: PayloadAction<string>) => {
      state.updateLoading = false;
      state.updatingCompanyId = null;
      state.error = action.payload;
      state.snackbar = { show: true, message: action.payload, type: 'Error' };
    },

    hideSnackbar: (state) => {
      state.snackbar.show = false;
    },
  },
});

export const {
  fetchCompanyListRequest,
  fetchCompanyListSuccess,
  fetchCompanyListFailure,
  updateCompanyStatusRequest,
  updateCompanyStatusSuccess,
  updateCompanyStatusFailure,
  hideSnackbar,
} = companyListSlice.actions;

export default companyListSlice.reducer;
